import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Stack,
  RadioGroup,
  Radio,
  Paper,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";

import { useDispatch, useSelector } from "react-redux";

// Actions from our unified templateCreationSlice
import {
  updateFormData,
  updateFieldsFlagAndMsg,
} from "../../../../redux/Slice/Templates/templateCreationSlice";
import { fetchUserPermissions } from "../../../../redux/Slice/User/userDetailsSlice";
import AuthView from "../../../../utils/AuthView";

const AuthenticationMainBody = () => {
  const dispatch = useDispatch();
  const {
    templateId,
    language,
    allowAutoCategoryChange,
    fieldsErrorMsg,
    // Authentication-specific fields from Redux
    codeDeliveryMethod,
    addSecurityRecommendation,
    addExpiryTime,
    expiryMinutes,
  } = useSelector((state) => state.templateCreation);
  const selectedCategory = useSelector(
    (state) => state.templateCreation.selectedCategory
  );
  const { userPermissions } = useSelector((state) => state.userDetails);

  // Check if user has permission to add templates
  const canAddTemplates = userPermissions?.permissions?.Templates?.Add || false;

  useEffect(() => {
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  // Local state to track validity of template ID
  const [templateIdError, setTemplateIdError] = useState(false);
  const [templateIdTouched, setTemplateIdTouched] = useState(false);

  // ====== Shared change helper ======
  const handleChange = useCallback((key, value) => {
    dispatch(updateFormData({ key, value }));
  }, [dispatch]);

  // ====== Template ID Handling ======
  const handleTemplateIdChange = (e) => {
    const value = e.target.value;

    // Example constraint: only a-z0-9_ up to 512 chars
    if (!/^[0-9a-z_]*$/.test(value)) {
      return; // reject invalid characters
    }
    if (value.length > 512) {
      return; // reject if too long
    }

    handleChange("templateId", value);
  };

  // Watch templateId and set local error - but only if the field has been touched
  useEffect(() => {
    if (templateIdTouched) {
      setTemplateIdError(!templateId.trim());
    }
  }, [templateId, templateIdTouched]);

  // Get button text based on delivery method
  const getButtonText = useCallback((method) => {
    switch (method) {
      case "COPY_CODE":
        return "Copy Code";
      case "ONE_TAP":
        return "Verify";
      case "ZERO_TAP":
        return "Verify";
      default:
        return "Copy Code";
    }
  }, []);

  // ====== Authentication Template Structure ======
  const generateAuthenticationComponents = useCallback(() => {
    const components = [];

    // BODY component with security recommendation
    components.push({
      type: "BODY",
      add_security_recommendation: addSecurityRecommendation
    });

    // FOOTER component with code expiration (only if enabled)
    if (addExpiryTime) {
      components.push({
        type: "FOOTER",
        code_expiration_minutes: expiryMinutes
      });
    }

    // BUTTONS component with OTP
    components.push({
      type: "BUTTONS",
      buttons: [
        {
          type: "OTP",
          otp_type: codeDeliveryMethod,
          text: getButtonText(codeDeliveryMethod)
        }
      ]
    });

    return components;
  }, [addSecurityRecommendation, addExpiryTime, expiryMinutes, codeDeliveryMethod, getButtonText]);

  // Update components whenever authentication settings change
  useEffect(() => {
    const components = generateAuthenticationComponents();
    handleChange("components", components);
  }, [addSecurityRecommendation, addExpiryTime, expiryMinutes, codeDeliveryMethod, generateAuthenticationComponents, handleChange]);

  // ====== Basic Field Validation => fieldsFlag & fieldsErrorMsg ======
  useEffect(() => {
    // Check template ID
    const templateIdFilled = !templateIdError && templateId.trim() !== "";

    // Gather missing fields
    const missingFields = [];
    if (!templateIdFilled) missingFields.push("• Template ID is required");

    // Dispatch result
    if (missingFields.length > 0) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: missingFields.join("\n"),
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: true,
          fieldsErrorMsg: "",
        })
      );
    }
  }, [
    templateIdError,
    templateId,
    dispatch,
  ]);

  // RENDER
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* If user doesn't have permission to add templates */}
      {!canAddTemplates ? (
        <AuthView />
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            Authentication Template Details
          </Typography>

          {/* Show an alert if there's a fieldsErrorMsg */}
          {fieldsErrorMsg && <Alert severity="error">{fieldsErrorMsg}</Alert>}

          {/* Template ID */}
          <TextField
            label="Template ID *"
            placeholder="Allowed: a-z, 0-9, _ (Max 512)"
            fullWidth
            required
            sx={{ maxWidth: 400 }}
            value={templateId}
            onChange={handleTemplateIdChange}
            onFocus={() => setTemplateIdTouched(true)}
            onBlur={() => setTemplateIdTouched(true)}
            error={templateIdTouched && templateIdError}
            helperText={
              templateIdTouched && templateIdError
                ? "Template ID is required and must contain only letters, numbers, and underscores."
                : "This field is required"
            }
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Selected Category"
              value={selectedCategory || "—"}
              disabled
              sx={{ maxWidth: 400 }}
            />
            <TextField
              label="Language"
              value={language}
              disabled
              sx={{ maxWidth: 400 }}
            />
          </Stack>

          {/* Checkbox: allowAutoCategoryChange */}
          <FormControlLabel
            control={
              <Checkbox
                checked={allowAutoCategoryChange}
                onChange={(e) =>
                  handleChange("allowAutoCategoryChange", e.target.checked)
                }
              />
            }
            label="Allow automatic category change"
          />

          {/* ==================== CODE DELIVERY SETUP ==================== */}
          <Paper sx={{ p: 3, border: 1, borderColor: 'primary.main' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon color="primary" />
              Code delivery setup
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Choose how customers send the code from WhatsApp to your app. Edits to this section won't require review or count towards edit limits.
            </Typography>
            
            <RadioGroup
              value={codeDeliveryMethod}
              onChange={(e) => handleChange("codeDeliveryMethod", e.target.value)}
            >
              <FormControlLabel
                value="COPY_CODE"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle2">Copy code</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Basic authentication with quick setup. Your customers copy and paste the code into your app.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="ONE_TAP"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle2">One-tap auto-fill</Typography>
                    <Typography variant="body2" color="textSecondary">
                      The code sends to your app when customers tap the button. A copy code message will be sent if auto-fill isn't possible.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="ZERO_TAP"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle2">Zero-tap auto-fill</Typography>
                    <Typography variant="body2" color="textSecondary">
                      This is recommended as the easiest option for your customers. Zero-tap will automatically send code without requiring your customer to tap a button. An auto-fill or copy code message will be sent if zero-tap and auto-fill aren't possible.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Paper>

          {/* ==================== CONTENT SETTINGS ==================== */}
          <Paper sx={{ p: 3, border: 1, borderColor: 'primary.main' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Content
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Content for authentication message templates can't be edited. You can add additional content from the options below.
            </Typography>
            
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addSecurityRecommendation}
                    onChange={(e) => handleChange("addSecurityRecommendation", e.target.checked)}
                  />
                }
                label="Add security recommendation"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addExpiryTime}
                    onChange={(e) => handleChange("addExpiryTime", e.target.checked)}
                  />
                }
                label="Should add expiry time for code"
              />
              
              {addExpiryTime && (
                <TextField
                  label="Expiry time (minutes)"
                  type="number"
                  value={expiryMinutes}
                  onChange={(e) => handleChange("expiryMinutes", parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 60 }}
                  sx={{ width: 200 }}
                />
              )}
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AuthenticationMainBody; 